import React, { useCallback, useEffect, useState } from "react";

import { useLocalization } from "@fluent/react";
import { Button, Row, TextInput } from "@narmi/design_system";
import { Dialog } from "cerulean";

const MAX_VIEW_NAME_LENGTH = 35;

interface SaveViewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
  validateName: (name: string) => string | null;
}

const SaveViewDialog = ({
  isOpen,
  onClose,
  onSave,
  validateName,
}: SaveViewDialogProps) => {
  const { l10n } = useLocalization();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setName("");
      setError(null);
      setIsSaving(false);
    }
  }, [isOpen]);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      if (value.length <= MAX_VIEW_NAME_LENGTH) {
        setName(value);
        setError(null);
      }
    },
    []
  );

  const handleSave = useCallback(async () => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      setError(l10n.getString("save-view-dialog-error-required"));
      return;
    }

    const validationError = validateName(trimmedName);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(trimmedName);
    } finally {
      setIsSaving(false);
    }
  }, [name, validateName, onSave, l10n]);

  return (
    <Dialog
      isOpen={isOpen}
      title={l10n.getString("save-view-dialog-title")}
      onUserDismiss={onClose}
      testId="save-view-dialog"
      footer={
        <Row alignItems="center" justifyContent="end" gapSize="s">
          <Row.Item shrink>
            <Button
              kind="plain"
              label={l10n.getString("save-view-dialog-button-cancel")}
              onClick={onClose}
              testId="save-view-dialog-cancel"
            />
          </Row.Item>
          <Row.Item shrink>
            <Button
              kind="primary"
              label={l10n.getString("save-view-dialog-button-save")}
              onClick={handleSave}
              disabled={isSaving}
              isLoading={isSaving}
              testId="save-view-dialog-save"
            />
          </Row.Item>
        </Row>
      }
    >
      <div className="margin--bottom--l">
        {l10n.getString("save-view-dialog-description")}
      </div>
      <TextInput
        label={l10n.getString("save-view-dialog-input-label")}
        value={name}
        onChange={handleNameChange}
        maxLength={MAX_VIEW_NAME_LENGTH}
        error={error}
        testId="save-view-dialog-name-input"
      />
    </Dialog>
  );
};

export default SaveViewDialog;
